"use client"

import * as React from "react"
import { AppSidebar } from "./app-sidebar"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "./breadcrumb"
import { Separator } from "./separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "./sidebar"
import { PatientChecklist, type ChecklistItem } from "./patient-checklist"
import { OnboardedPatientDashboard, type MedicationInfo, type VisitInfo, type AppointmentInfo } from "./onboarded-patient-dashboard"
import { MedicationCard } from "./medication-card"
import { Button } from "./button"
import { Alert, AlertTitle, AlertDescription } from "./alert"
import { MedicationPaymentCard } from "./medication-payment-card"
import { MedicationActiveSubscriptionCard } from "./atomic/molecules/medications/active-subscription"
import { MedicationSubscriptionAdjustedCard } from "./medication-subscription-adjusted-card"
import { PhoneNumberAlert } from "./phone-number-alert"
import { PrescriptionDueAlert } from "./prescription-due-alert"
import { usePhoneNumberCheck } from "../hooks/usePhoneNumberCheck"
import { supabase, StripeService, useAuth } from "@joinomu/shared"

export function PatientDashboard({
  user,
  onLogout,
  checklistItems,
  onChecklistItemClick,
  isOnboarded = false,
  medications,
  visits,
  appointment,
  selectedCategory,
  onCategoryChange,
  onRescheduleVisit,
  onRescheduleAppointment,
  onMedicationAction,
  showAverageResults,
  paymentRequired,
  paymentDueDate,
  activeItem = "Dashboard",
  onNavigate,
  // Simple card data for post-onboarding
  realMedicationData,
  realAppointmentData,
  onEditMedication,
  onEditAppointment,
  onAddMedication,
  onAccountClick,
  onBillingClick,
  onPreferencesClick,
  onHealthProfileClick,
  onSupportFeedbackClick,
  onSubscriptionAdjustedOk,
  onSubscriptionAdjustedView,
  isFreePlan,
  patientAddresses = [],
  onAddShippingAddress,
  onOpenAppointmentDialog
}: {
  user?: {
    id?: string
    name: string
    email: string
    avatar?: string
  }
  onLogout?: () => void
  checklistItems?: ChecklistItem[]
  onChecklistItemClick?: (item: ChecklistItem) => void
  isOnboarded?: boolean
  medications?: MedicationInfo[]
  visits?: VisitInfo[]
  appointment?: AppointmentInfo
  selectedCategory?: 'weightloss' | 'mens-health' | 'all'
  onCategoryChange?: (category: 'weightloss' | 'mens-health' | 'all') => void
  onRescheduleVisit?: (visit: VisitInfo) => void
  onRescheduleAppointment?: () => void
  onMedicationAction?: () => void
  showAverageResults?: boolean
  paymentRequired?: boolean
  paymentDueDate?: string
  activeItem?: string
  onNavigate?: (item: string) => void
  // Simple card data for post-onboarding - support multiple medications
  realMedicationData?: {
    id: string
    medicationId?: string
    name: string
    dosage: string
    supply: string
    supply_days?: number
    status: 'pending' | 'approved' | 'denied'
    estimatedDelivery?: string
    nextPrescriptionDue?: string
    refillRequested?: boolean
    refillRequestedDate?: string
    visitRequested?: boolean
    visitRequestedDate?: string
    approvalDate?: string
    fulfillmentStatus?: string
    medicationInfo?: any
    dosageInfo?: any
    isRecentlyAdjusted?: boolean
    providerId?: string  // Provider who approved this medication
    // Add subscription fields
    subscription_status?: 'active' | 'inactive' | 'trialing' | 'past_due' | 'canceled'
    stripe_subscription_id?: string
    // Add snake_case fields as fallbacks for data transformation issues
    refill_requested?: boolean
    refill_requested_date?: string
    visit_requested?: boolean
    visit_requested_date?: string
    approval_date?: string
    next_prescription_due?: string
    provider_id?: string  // Snake case fallback
  }[]
  realAppointmentData?: {
    id: string
    doctorName: string
    visitType: string
    dateTime: string
    status: 'scheduled' | 'active' | 'pending'
    appointmentDate: string
    startTime: string
    providerId: string
    treatmentType: string
    appointmentType: string
  }[]
  onEditMedication?: (medicationId?: string) => void
  onEditAppointment?: (appointmentId?: string) => void
  onAddMedication?: () => void
  onAccountClick?: () => void
  onBillingClick?: () => void
  onPreferencesClick?: () => void
  onHealthProfileClick?: () => void
  onSupportFeedbackClick?: (type: "Support" | "Feedback") => void
  onSubscriptionAdjustedOk?: (medicationId: string) => void
  onSubscriptionAdjustedView?: (medicationId: string) => void
  isFreePlan?: boolean
  patientAddresses?: any[]
  onAddShippingAddress?: () => void
  onOpenAppointmentDialog?: (providerId?: string) => void
}) {
  // Get current user context to check role
  const { userRole } = useAuth()
  
  // Check if user has phone number
  const { hasPhoneNumber, loading: phoneCheckLoading, recheckPhoneNumber } = usePhoneNumberCheck()
  
  // Determine if user should be able to edit medications (only non-patient users)
  const canEditMedications = userRole !== 'patient'

  // Map appointment/visit status to medication card status
  const mapStatus = (status: string): 'pending' | 'approved' | 'denied' | 'active' | 'discontinued' | 'shipped' | 'delivered' | 'scheduled' => {
    switch (status) {
      case 'confirmed': return 'active'
      case 'scheduled': return 'scheduled'
      case 'pending': return 'pending'
      default: return 'pending'
    }
  }

  // Handle opening account dialog and automatically navigating to account section
  const handleOpenAccountForPhone = React.useCallback(() => {
    onAccountClick?.()
    // Recheck phone number when account dialog opens
    setTimeout(() => {
      recheckPhoneNumber()
    }, 500)
  }, [onAccountClick, recheckPhoneNumber])

  return (
    <SidebarProvider>
        <AppSidebar 
          user={user} 
          onLogout={onLogout} 
          onNavigate={onNavigate} 
          onAccountClick={onAccountClick}
          onBillingClick={onBillingClick}
          onPreferencesClick={onPreferencesClick}
          onHealthProfileClick={onHealthProfileClick}
          onSupportFeedbackClick={onSupportFeedbackClick}
        />
        <SidebarInset>
          <header className="flex h-16 shrink-0 items-center gap-2">
            <div className="flex items-center gap-2 px-4">
              <SidebarTrigger className="-ml-1" />
              <Separator
                orientation="vertical"
                className="mr-2 data-[orientation=vertical]:h-4"
              />
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbPage>Dashboard</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          </header>
          <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
            
            {!isOnboarded && checklistItems ? (
              <div className="flex justify-center">
                <PatientChecklist 
                  items={checklistItems}
                  onItemClick={onChecklistItemClick}
                />
              </div>
            ) : (
              <>
                {/* Onboarded patient view with simple cards */}
                <div className="space-y-6 w-full">
                  
                  {/* Free Plan Upgrade Section */}
                  {isFreePlan && (
                    <div className="flex flex-col items-center justify-center space-y-6 py-12 px-4">
                      <div className="text-center space-y-4">
                        <p className="text-gray-500 text-sm max-w-lg leading-relaxed">
                          Provider visits and medications require an active plan, tracking doesn't
                        </p>
                        <Button
                          onClick={() => onNavigate?.('/pricing')}
                        >
                          View Plans
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Medication Cards Section */}
                  {!isFreePlan && (realMedicationData?.length > 0 || (medications && medications.length > 0)) && (
                    <div className="space-y-4 w-full">
                      <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Medications</h2>
                        {onAddMedication && (
                          <Button
                            variant="link"
                            onClick={onAddMedication}
                            className="text-base font-medium p-0 h-auto"
                          >
                            Add Medication
                          </Button>
                        )}
                      </div>
                      
                      {/* Payment Alerts for medications with null/empty brand_name that either have no next_prescription_due date OR are within 15 days of next_prescription_due */}
                      {(() => {
                        // Find ALL medications that need payment
                        const medicationsNeedingPayment = realMedicationData?.filter(med => {

                          // EXCLUDE Doctor Recommendation medications - they don't need payment
                          if (med.name === 'Doctor Recommendation' || med.medicationInfo?.name === 'Doctor Recommendation') {
                            return false
                          }

                          // EXCLUDE Wegovy, Ozempic, and Zepbound medications - they don't need payment buttons
                          const medicationName = med.name || med.medicationInfo?.name || ''
                          if (['Wegovy', 'Ozempic', 'Zepbound', 'Wegovy Pill'].includes(medicationName)) {
                            return false
                          }

                          const brandName = med.medicationInfo?.brand_name
                          const hasNullBrandName = !brandName || brandName.trim() === ''

                          // Only consider subscription-based medications (null brand_name)
                          if (!hasNullBrandName) {
                            return false
                          }

                          // Show payment alert if NO active subscription
                          const validSubscriptionStatuses = ['active', 'trialing', 'past_due']
                          if (!med.subscription_status || !validSubscriptionStatuses.includes(med.subscription_status)) {
                            return true
                          }

                          // If there's an active subscription, check if renewal payment is due soon
                          const nextDue = med.nextPrescriptionDue
                          if (!nextDue) {
                            return true
                          }

                          // Check if next_prescription_due is within 15 days (renewal payment needed)
                          const dueDate = new Date(nextDue)
                          const today = new Date()
                          const diffTime = dueDate.getTime() - today.getTime()
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

                          // For active subscriptions within 15 days, don't show payment alert - they should see active subscription instead
                          if (diffDays <= 15) {
                            return false
                          }

                          return false
                        }) || []

                        if (medicationsNeedingPayment.length === 0) {
                          return null
                        }

                        // Return a payment alert for EACH medication that needs payment
                        return medicationsNeedingPayment.map(med => {
                          // Determine variant based on nextPrescriptionDue
                          const nextDue = med.nextPrescriptionDue
                          const variant: 'shipping' | 'next_month' = nextDue ? 'next_month' : 'shipping'

                          // Determine subscription type based on supply days
                          const supplyDays = med.supply_days || 30
                          const subscriptionType: 'monthly' | 'quarterly' = supplyDays <= 45 ? 'monthly' : 'quarterly'
                          const isQuarterly = supplyDays > 45


                          return (
                            <MedicationPaymentCard
                              key={`payment-${med.id}`}
                              medicationName={med.name}
                              dosage={med.dosage}
                              supply={med.supply}
                              status={med.status}
                              estimatedDelivery={med.estimatedDelivery}
                              fulfillmentStatus={med.fulfillmentStatus}
                              nextPrescriptionDue={med.nextPrescriptionDue}
                              variant={variant}
                              onTitleClick={canEditMedications ? () => onEditMedication?.(med.id) : undefined}
                              hasShippingAddress={patientAddresses && patientAddresses.length > 0}
                              onAddShippingAddress={onAddShippingAddress}
                              className="w-full max-w-full"
                              onPaymentClick={async () => {
                                try {
                                  // Check if user is available
                                  if (!user?.id) {
                                    alert('Please refresh the page and try again.')
                                    return
                                  }

                                  if (!med.medicationInfo) {
                                    return
                                  }

                                  // Get appropriate price ID from dosage data
                                  const priceId = await (async () => {
                                    try {
                                      // Get the dosage information for this medication
                                      const dosageInfo = med.dosageInfo
                                      if (dosageInfo?.stripe_price_id) {
                                        return dosageInfo.stripe_price_id
                                      }

                                      // Fallback: query medication_dosages table for first available price
                                      const { data: dosages, error } = await supabase
                                        .from('medication_dosages')
                                        .select('stripe_price_id, strength')
                                        .eq('medication_id', med.medicationId)
                                        .not('stripe_price_id', 'is', null)
                                        .order('sort_order')
                                        .limit(1)

                                      if (error) {
                                        return null
                                      }

                                      if (dosages && dosages.length > 0) {
                                        return dosages[0].stripe_price_id
                                      }

                                      return null
                                    } catch (error) {
                                      return null
                                    }
                                  })()

                                  if (!priceId) {
                                    return
                                  }

                                  // Use same patient lookup as pricing page
                                  const { data: patient, error: patientError } = await supabase
                                    .from('patients')
                                    .select('id')
                                    .eq('profile_id', user.id)
                                    .single()

                                  if (patientError || !patient) {
                                    alert('Unable to find patient data. Please refresh and try again.')
                                    return
                                  }

                                  const patientId = patient.id

                                  // Use StripeService directly like the working pricing page
                                  const result = await StripeService.createCheckoutSession({
                                    priceId,
                                    patientId,
                                    successUrl: `${window.location.origin}${window.location.pathname}?payment=success&medication=${encodeURIComponent(med.name)}&type=${isQuarterly ? 'quarterly' : 'monthly'}`,
                                    cancelUrl: `${window.location.origin}${window.location.pathname}?payment=cancelled`
                                  })

                                  if (result.success && result.url) {
                                    // Redirect to Stripe Checkout
                                    window.location.href = result.url
                                  } else {
                                    throw new Error(result.error || 'Failed to create checkout session')
                                  }
                                } catch (error) {
                                  alert('Failed to start checkout. Please try again.')
                                }
                              }}
                            />
                          )
                        })
                      })()}

                      {/* Active Subscription Cards for medications that have been paid for */}
                      {(() => {
                        // Find medications that have active subscriptions (paid but not needing immediate payment)
                        const medicationsWithActiveSubscription = realMedicationData?.filter(med => {
                          const brandName = med.medicationInfo?.brand_name
                          const hasNullBrandName = !brandName || brandName.trim() === ''
                          
                          // Only consider subscription-based medications (null brand_name)
                          if (!hasNullBrandName) {
                            return false
                          }

                          // Must have active subscription status
                          const validSubscriptionStatuses = ['active', 'trialing', 'past_due']
                          if (!med.subscription_status || !validSubscriptionStatuses.includes(med.subscription_status)) {
                            return false
                          }
                          
                          const nextDue = med.nextPrescriptionDue
                          
                          // If no next_prescription_due date, payment is needed first (don't show active subscription)
                          if (!nextDue) {
                            return false
                          }
                          
                          // Show active subscription for ANY active subscription with a due date
                          return true
                        }) || []

                        return medicationsWithActiveSubscription.map(med => (
                          <MedicationActiveSubscriptionCard
                            key={`active-subscription-${med.id}`}
                            medicationName={med.name}
                            dosage={med.dosage}
                            supply={med.supply}
                            status="active"
                            estimatedDelivery={med.estimatedDelivery}
                            fulfillmentStatus={med.fulfillmentStatus}
                            nextPrescriptionDue={med.nextPrescriptionDue}
                            onTitleClick={canEditMedications ? () => onEditMedication?.(med.id) : undefined}
                            onBillingClick={onBillingClick}
                            hasShippingAddress={patientAddresses && patientAddresses.length > 0}
                            onAddShippingAddress={onAddShippingAddress}
                            className="w-full max-w-full"
                          />
                        ))
                      })()}
                      
                      {/* Display subscription adjusted cards for recently adjusted medications */}
                      {realMedicationData?.filter(medication => {
                        return medication.isRecentlyAdjusted === true
                      }).map((medication) => (
                        <MedicationSubscriptionAdjustedCard
                          key={`subscription-adjusted-${medication.id}`}
                          medicationName={medication.name}
                          dosage={medication.dosage}
                          supply={medication.supply}
                          status={medication.status}
                          onViewClick={() => onSubscriptionAdjustedView?.(medication.id)}
                          onOkClick={() => onSubscriptionAdjustedOk?.(medication.id)}
                          onTitleClick={canEditMedications ? () => onEditMedication?.(medication.id) : undefined}
                          className="w-full"
                        />
                      ))}
                      
                      {/* Display medication cards for medications that DON'T need payment and DON'T have active subscriptions and are NOT recently adjusted */}
                      {realMedicationData?.filter(medication => {
                        // Don't show regular cards for recently adjusted medications
                        if (medication.isRecentlyAdjusted) {
                          return false
                        }
                        
                        // Only show regular cards for medications that don't need payment and don't have active subscriptions
                        const brandName = medication.medicationInfo?.brand_name
                        const hasNullBrandName = !brandName || brandName.trim() === ''
                        
                        if (!hasNullBrandName) {
                          return true // Has brand name, show regular card
                        }
                        
                        // Special case: Ozempic, Wegovy, and Zepbound never need payment - always show as regular cards
                        const medicationName = medication.name || medication.medicationInfo?.name || ''
                        if (['Wegovy', 'Ozempic', 'Zepbound', 'Wegovy Pill'].includes(medicationName)) {
                          return true
                        }
                        
                        const nextDue = medication.nextPrescriptionDue
                        
                        // If no next_prescription_due, needs payment (don't show regular card)
                        if (!nextDue) {
                          return false
                        }
                        
                        // Check if within 15 days of due date
                        const dueDate = new Date(nextDue)
                        const today = new Date()
                        const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
                        
                        // If within 15 days, needs payment (don't show regular card)
                        if (diffDays <= 15) {
                          return false
                        }
                        
                        // If more than 15 days away, shows as active subscription (don't show regular card)
                        if (diffDays > 15) {
                          return false
                        }
                        
                        return true // Fallback to regular card
                      }).map((medication) => (
                        <MedicationCard
                          key={medication.id}
                          medicationName={medication.name}
                          dosage={medication.dosage}
                          supply={medication.supply}
                          status={medication.status}
                          estimatedDelivery={medication.estimatedDelivery}
                          fulfillmentStatus={medication.fulfillmentStatus}
                          nextPrescriptionDue={medication.nextPrescriptionDue}
                          onTitleClick={canEditMedications ? () => onEditMedication?.(medication.id) : undefined}
                          className="w-full"
                        />
                      ))}
                      {/* Show legacy medications if available */}
                      {medications && medications.map((med, index) => (
                        <MedicationCard
                          key={`med-${index}`}
                          medicationName={med.name}
                          dosage={med.dosage}
                          supply="30 day supply"
                          status={med.status}
                          onTitleClick={canEditMedications ? () => onEditMedication?.() : undefined}
                          className="w-full"
                        />
                      ))}
                    </div>
                  )}
                  
                  {/* Add Medication Section when no medications exist */}
                  {!isFreePlan && !realMedicationData?.length && (!medications || medications.length === 0) && onAddMedication && (
                    <div className="space-y-4 w-full">
                      <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Medications</h2>
                        <Button
                          variant="link"
                          onClick={onAddMedication}
                          className="text-base font-medium p-0 h-auto"
                        >
                          Add Medication
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Visit/Appointment Cards Section */}
                  <div className="space-y-4 w-full">
                    <h2 className="text-lg font-semibold">Your Scheduled Visits</h2>
                      
                      {/* Phone Number Alert - Show only for onboarded users who are missing phone number */}
                      {isOnboarded && !phoneCheckLoading && !hasPhoneNumber && (
                        <PhoneNumberAlert
                          show={true}
                          onOpenAccount={handleOpenAccountForPhone}
                          className="mb-4"
                        />
                      )}
                      
                      {/* Prescription Due Alert - Show when medication is due within a week and no upcoming appointment */}
                      {(() => {
                        console.log('🔍 PRESCRIPTION ALERT DEBUG - Starting check...')
                        console.log('🔍 isOnboarded:', isOnboarded)
                        console.log('🔍 realMedicationData?.length:', realMedicationData?.length)
                        
                        if (!isOnboarded || !realMedicationData?.length) {
                          console.log('❌ PRESCRIPTION ALERT: Early return - not onboarded or no medication data')
                          return null
                        }
                        
                        const today = new Date()
                        const oneWeekFromNow = new Date()
                        oneWeekFromNow.setDate(today.getDate() + 7)
                        
                        console.log('🔍 PRESCRIPTION ALERT DEBUG - Today:', today)
                        console.log('🔍 PRESCRIPTION ALERT DEBUG - One week from now:', oneWeekFromNow)
                        
                        // Find medications due within a week
                        const medicationsDueWithinWeek = realMedicationData.filter(med => {
                          console.log('🔍 PRESCRIPTION ALERT DEBUG - Checking medication:', med.name)
                          console.log('🔍 PRESCRIPTION ALERT DEBUG - nextPrescriptionDue:', med.nextPrescriptionDue)
                          
                          if (!med.nextPrescriptionDue) {
                            console.log('❌ PRESCRIPTION ALERT: No nextPrescriptionDue for', med.name)
                            return false
                          }
                          
                          const dueDate = new Date(med.nextPrescriptionDue)
                          const isDueWithinWeek = dueDate <= oneWeekFromNow && dueDate >= today
                          console.log('🔍 PRESCRIPTION ALERT DEBUG - Due date:', dueDate)
                          console.log('🔍 PRESCRIPTION ALERT DEBUG - Is due within week:', isDueWithinWeek)
                          
                          return isDueWithinWeek
                        })
                        
                        console.log('🔍 PRESCRIPTION ALERT DEBUG - Medications due within week:', medicationsDueWithinWeek.length)
                        
                        if (medicationsDueWithinWeek.length === 0) {
                          console.log('❌ PRESCRIPTION ALERT: No medications due within a week')
                          return null
                        }
                        
                        // Check if patient has upcoming appointments BEFORE the medication due date
                        // If appointment is after the due date, patient still needs to schedule an earlier visit
                        const firstMedDue = medicationsDueWithinWeek[0]
                        const dueDate = new Date(firstMedDue.nextPrescriptionDue)
                        
                        // Get the provider ID from the medication preference first
                        const medicationProviderId = firstMedDue.providerId || firstMedDue.provider_id
                        
                        console.log('🔍 PRESCRIPTION ALERT DEBUG - First med due:', firstMedDue.name)
                        console.log('🔍 PRESCRIPTION ALERT DEBUG - Due date:', dueDate)
                        console.log('🔍 PRESCRIPTION ALERT DEBUG - Medication provider ID:', medicationProviderId)
                        console.log('🔍 PRESCRIPTION ALERT DEBUG - Appointment data:', realAppointmentData)
                        
                        // Check if patient has ANY FUTURE appointment with the SPECIFIC provider
                        // This allows multiple providers while showing alert only for relevant provider
                        const hasAppointmentWithProvider = realAppointmentData?.some(appt => {
                          const appointmentProviderId = appt.providerId
                          
                          console.log('🔍 PRESCRIPTION ALERT DEBUG - Checking appointment:', appt.id)
                          console.log('🔍 PRESCRIPTION ALERT DEBUG - Appointment provider ID:', appointmentProviderId)
                          console.log('🔍 PRESCRIPTION ALERT DEBUG - Appointment status:', appt.status)
                          
                          // Only consider future appointments - past appointments shouldn't hide the alert
                          const appointmentDate = new Date(appt.appointmentDate || appt.dateTime)
                          const isFutureAppointment = appointmentDate > today
                          
                          console.log('🔍 PRESCRIPTION ALERT DEBUG - Appointment date:', appointmentDate)
                          console.log('🔍 PRESCRIPTION ALERT DEBUG - Is future appointment:', isFutureAppointment)
                          
                          const matches = ['scheduled', 'confirmed'].includes(appt.status) &&
                                 appointmentProviderId === medicationProviderId && // Must match provider
                                 isFutureAppointment // Must be a future appointment
                          
                          console.log('🔍 PRESCRIPTION ALERT DEBUG - Appointment matches criteria:', matches)
                          
                          return matches
                        })
                        
                        console.log('🔍 PRESCRIPTION ALERT DEBUG - Has appointment with provider:', hasAppointmentWithProvider)
                        
                        if (hasAppointmentWithProvider) {
                          console.log('❌ PRESCRIPTION ALERT: Has appointment with provider - hiding alert')
                          return null
                        }
                        
                        console.log('✅ PRESCRIPTION ALERT: Showing alert for', firstMedDue.name)
                        
                        return (
                          <PrescriptionDueAlert
                            show={true}
                            medicationName={firstMedDue.name}
                            dueDate={dueDate}
                            onScheduleVisit={() => onOpenAppointmentDialog?.(medicationProviderId)}
                            className="mb-4"
                          />
                        )
                      })()}
                      
                      {/* Display all real appointment data */}
                      {realAppointmentData?.map((appointment) => (
                        <MedicationCard
                          key={appointment.id}
                          medicationName={appointment.doctorName}
                          dosage={appointment.visitType}
                          supply={appointment.dateTime}
                          status={mapStatus(appointment.status)}
                          onTitleClick={() => onEditAppointment?.(appointment.id)}
                          className="w-full"
                        />
                      ))}
                      {/* Show multiple visits if available */}
                      {visits && visits.map((visit, index) => (
                        <MedicationCard
                          key={`visit-${index}`}
                          medicationName={visit.doctorName}
                          dosage={visit.type}
                          supply={`${visit.date} at ${visit.time}`}
                          status={mapStatus(visit.status)}
                          onTitleClick={onEditAppointment}
                          className="w-full"
                        />
                      ))}
                      {/* Fallback single appointment */}
                      {appointment && !visits && (
                        <MedicationCard
                          medicationName={appointment.doctorName}
                          dosage={appointment.type}
                          supply={`${appointment.date} at ${appointment.time}`}
                          status={mapStatus(appointment.status)}
                          onTitleClick={onEditAppointment}
                          className="w-full"
                        />
                      )}
                      
                      {/* No visits message */}
                      {!realAppointmentData?.length && !visits?.length && !appointment && (
                        <div className="text-center py-8 text-gray-500">
                          <p>You don't have any visits scheduled</p>
                        </div>
                      )}
                    </div>
                </div>
                
                {/* Consent Terms */}
                <div className="mt-8 pt-6 border-t">
                  <p className="text-xs text-gray-500 text-center leading-relaxed">
                    By using JoinOmu you are accepting our{" "}
                    <a 
                      href="https://joinomu.com/telehealth-consent" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-gray-800 underline"
                    >
                      Telehealth Consent
                    </a>{" "}
                    and{" "}
                    <a 
                      href="https://joinomu.com/weight-loss-consent" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-gray-800 underline"
                    >
                      Weight Loss Consent
                    </a>{" "}
                    terms and acknowledge you understand the risks and potential benefits of starting a weight loss program.
                  </p>
                </div>
                
                {/* Additional dashboard content */}
                <div className="grid auto-rows-min gap-4 md:grid-cols-3">
                  <div className="bg-muted/50 aspect-video rounded-xl" />
                  <div className="bg-muted/50 aspect-video rounded-xl" />
                  <div className="bg-muted/50 aspect-video rounded-xl" />
                </div>
                <div className="bg-muted/50 min-h-[100vh] flex-1 rounded-xl md:min-h-min" />
              </>
            )}
          </div>
        </SidebarInset>
    </SidebarProvider>
  )
}